import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  MessageSquare, Clock, CheckCircle, XCircle, User, MapPin,
  Briefcase, ArrowRight, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  declined: 'bg-red-50 text-red-500 border-red-200',
};

const STATUS_ICONS = {
  pending: <Clock className="h-3.5 w-3.5" />,
  accepted: <CheckCircle className="h-3.5 w-3.5" />,
  declined: <XCircle className="h-3.5 w-3.5" />,
};

const BuyerConsultations = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/consultations/my-requests`);
      setConsultations(res.data);
    } catch {
      toast.error('Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (consultations.length === 0) {
    return (
      <div className="luxury-card p-12 text-center" data-testid="no-consultations">
        <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 text-lg font-['Playfair_Display']">No consultation requests yet</p>
        <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
          Visit the <a href="/mortgage-guide" className="text-[#E07A3A] hover:underline font-medium">Mortgage Guide</a> to find professionals and request a free consultation.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="buyer-consultations-list">
      {consultations.map((c) => (
        <div key={c.id} className="luxury-card overflow-hidden" data-testid={`consultation-${c.id}`}>
          <div className="p-5">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h3 className="text-[#1A1A1A] font-medium text-lg">{c.professional_name}</h3>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-0.5 text-xs tracking-wider uppercase font-medium border ${STATUS_STYLES[c.status]}`}>
                    {STATUS_ICONS[c.status]}
                    {c.status}
                  </span>
                </div>
                <p className="text-xs text-[#E07A3A] font-semibold uppercase tracking-wider">
                  {(c.service_type || '').replace(/_/g, ' ')}
                </p>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                  {c.country && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{c.country}</span>}
                  {c.created_at && <span><Clock className="h-3 w-3 inline mr-1" />{new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-slate-200 text-slate-500 hover:text-[#E07A3A] hover:border-[#E07A3A]/30 text-xs"
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                data-testid={`toggle-details-${c.id}`}
              >
                Details {expanded === c.id ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
              </Button>
            </div>

            {/* Expanded details */}
            {expanded === c.id && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                {c.message && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Your Message</p>
                    <p className="text-sm text-slate-600 italic">"{c.message}"</p>
                  </div>
                )}
                {c.response_message && (
                  <div className="bg-slate-50 p-4 border-l-3 border-l-[#E07A3A]" style={{ borderLeft: '3px solid #E07A3A' }}>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Professional's Response</p>
                    <p className="text-sm text-slate-700">"{c.response_message}"</p>
                  </div>
                )}
                {c.prequalification_summary && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Pre-Qualification Shared</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div className="bg-slate-50 p-2 text-center">
                        <p className="text-xs text-slate-400">Max Borrowing</p>
                        <p className="text-sm font-semibold text-[#0F172A]">{c.prequalification_summary.symbol}{c.prequalification_summary.max_borrowing?.toLocaleString('en-GB')}</p>
                      </div>
                      <div className="bg-slate-50 p-2 text-center">
                        <p className="text-xs text-slate-400">Monthly Payment</p>
                        <p className="text-sm font-semibold text-[#0F172A]">{c.prequalification_summary.symbol}{c.prequalification_summary.estimated_monthly_payment?.toLocaleString('en-GB')}</p>
                      </div>
                      <div className="bg-slate-50 p-2 text-center">
                        <p className="text-xs text-slate-400">LTV Ratio</p>
                        <p className="text-sm font-semibold text-[#0F172A]">{c.prequalification_summary.ltv_ratio}%</p>
                      </div>
                    </div>
                  </div>
                )}
                {c.property_info && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Property</p>
                    <p className="text-sm text-slate-600">{c.property_info.title} — {c.property_info.city}</p>
                  </div>
                )}
                {c.responded_at && (
                  <p className="text-xs text-slate-400">Responded on {new Date(c.responded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BuyerConsultations;
